import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Heart, ShoppingCart } from 'lucide-react'
import LazyImage from '@/components/ui/LazyImage'
import { formatPrice } from '@/lib/utils'

/**
 * Related Products Component
 * Displays "You may be interested in" section with random products
 * - First tries to show products from the same category
 * - Falls back to random products from other categories if none available
 * - Mobile-first responsive design
 */

export function RelatedProducts({ 
  currentProductId, 
  currentProductCategory,
  products = [],
  maxItems = 4,
  onAddToWishlist,
  onAddToCart,
  currency = '₦'
}) {
  const [relatedProducts, setRelatedProducts] = useState([])
  const [loading, setLoading] = useState(true)

  const getProductPrice = (product) => {
    const raw = product.sellingprice ?? product.sell_price ?? product.price ?? product?.price ?? 0
    return Number(raw) || 0
  }

  const getProductOriginalPrice = (product) => {
    const raw = product.originalPrice ?? product.mrp ?? product.original_price ?? product.list_price ?? 0
    return Number(raw) || 0
  }

  const displayCurrency = currency === 'NGN' ? '₦' : currency

  useEffect(() => {
    // Filter products excluding the current one
    const otherProducts = products.filter(p => p.id !== currentProductId)

    if (otherProducts.length === 0) {
      setRelatedProducts([])
      setLoading(false)
      return
    }

    // Try to get products from the same category
    const sameCategory = otherProducts.filter(
      p => p.category === currentProductCategory && p.category !== 'Nil'
    )

    let selected = []

    if (sameCategory.length > 0) {
      // Shuffle and get up to maxItems from same category
      const shuffled = [...sameCategory].sort(() => 0.5 - Math.random())
      selected = shuffled.slice(0, maxItems)
    } else {
      // If not enough from same category, fill with random from other categories
      const shuffled = [...otherProducts].sort(() => 0.5 - Math.random())
      selected = shuffled.slice(0, maxItems)
    }

    setRelatedProducts(selected)
    setLoading(false)
  }, [currentProductId, currentProductCategory, products, maxItems])

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6">
        <div className="h-8 bg-gray-200 rounded w-48 mb-6 animate-pulse" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-3">
              <div className="aspect-square bg-gray-200 rounded-lg animate-pulse" />
              <div className="h-4 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (relatedProducts.length === 0) {
    return null
  }

  return (
    <section className="mt-12 sm:mt-16">
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 sm:p-8">
        <div className="mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            You may be interested in
          </h2>
          <p className="text-gray-600 text-sm sm:text-base">
            Similar products that you might like
          </p>
        </div>

        {/* Mobile: Single column with horizontal scroll */}
        <div className="block sm:hidden">
          <div className="overflow-x-auto -mx-6 sm:mx-0 px-6 sm:px-0">
            <div className="flex gap-4 pb-4 w-max">
              {relatedProducts.map((product) => (
                <div
                  key={product.id}
                  className="flex-shrink-0 w-40 bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                >
                  {/* Product Image */}
                  <Link to={`/product/${product.id}`} className="block">
                    <div className="relative aspect-square bg-gray-100 overflow-hidden group">
                      {product.images && product.images.length > 0 ? (
                        <LazyImage src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-200">
                          <span className="text-gray-400 text-xs">No image</span>
                        </div>
                      )}
                      {product.discount && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                          -{product.discount}%
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Product Info */}
                  <div className="p-3">
                    <Link to={`/product/${product.id}`}>
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 hover:text-blue-600 transition-colors">
                        {product.name}
                      </h3>
                    </Link>

                    {/* Price */}
                    <div className="mt-2 mb-3">
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg font-bold text-gray-900">
                          {displayCurrency}{formatPrice(getProductPrice(product))}
                        </span>
                        {getProductOriginalPrice(product) > 0 && (
                          <span className="text-xs text-gray-500 line-through">
                            {displayCurrency}{formatPrice(getProductOriginalPrice(product))}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <Link
                      to={`/product/${product.id}`}
                      className="w-full py-2 bg-brand-600 text-white text-xs font-semibold rounded hover:bg-brand-700 transition-colors flex items-center justify-center gap-1"
                    >
                      View
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tablet and Desktop: Grid layout */}
        <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {relatedProducts.map((product) => (
            <div
              key={product.id}
              className="group bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              {/* Product Image */}
              <Link to={`/product/${product.id}`} className="block">
                <div className="relative aspect-square bg-gray-100 overflow-hidden">
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <span className="text-gray-400">No image</span>
                    </div>
                  )}

                  {/* Discount Badge */}
                  {product.discount && (
                    <div className="absolute top-3 right-3 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                      -{product.discount}%
                    </div>
                  )}

                  {/* Wishlist Button */}
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      onAddToWishlist?.(product)
                    }}
                    className="absolute top-3 left-3 p-2 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-gray-100"
                  >
                    <Heart className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </Link>

              {/* Product Info */}
              <div className="p-4">
                <Link to={`/product/${product.id}`}>
                  <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                    {product.name}
                  </h3>
                </Link>

                {/* Price */}
                <div className="mt-3 mb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold text-gray-900">
                      {displayCurrency}{formatPrice(getProductPrice(product))}
                    </span>
                    {getProductOriginalPrice(product) > 0 && (
                      <span className="text-sm text-gray-500 line-through">
                        {displayCurrency}{formatPrice(getProductOriginalPrice(product))}
                      </span>
                    )}
                  </div>
                </div>

                {/* Rating (if available) */}
                {product.rating && (
                  <div className="mb-4 flex items-center gap-1">
                    <div className="flex text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <span key={i}>
                          {i < Math.floor(product.rating) ? '★' : '☆'}
                        </span>
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">
                      ({product.reviewCount || 0})
                    </span>
                  </div>
                )}

                {/* View Button */}
                <Link
                  to={`/product/${product.id}`}
                  className="w-full py-2.5 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600 text-white font-semibold rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                >
                  View
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* View More Link */}
        <div className="mt-8 flex justify-center">
          <Link
            to="/products"
            className="px-6 py-3 border-2 border-blue-600 text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
          >
            View All Products
          </Link>
        </div>
      </div>
    </section>
  )
}

export default RelatedProducts
